import type { FC } from 'react'
import { useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button } from '../../components/Buttons/Button'
import { ActionButtons } from '../../components/ChargingSteering/ActionButtons'
import { SteeringModeBar } from '../../components/ChargingSteering/SteeringModeBar'
import { TelemetryMessagesPanel } from '../../components/ChargingSteering/TelemetryMessagesPanel'
import { InfoBlock } from '../../components/InfoBlock/InfoBlock'
import { LoadingIndicator } from '../../components/LoadingIndicator/LoadingIndicator'
import { VehicleHeader } from '../../components/VehicleHeader/VehicleHeader'
import {
  fetchFeature2ChargingCommandStatusThunk,
  fetchFeature2ChargingKeysThunk,
  setFeature2CommandIdInput,
  setFeature2ResumeChargingAt,
  setFeature2ResumeChargingAtEnabled,
  setFeature2Vin,
  startFeature2ChargingThunk,
  stopFeature2ChargingThunk,
} from '../../features/feature2ChargingSteering/feature2ChargingSteeringSlice'
import {
  selectFeature2CommandError,
  selectFeature2CommandIdInput,
  selectFeature2CommandLoading,
  selectFeature2CommandResponse,
  selectFeature2CommandStatusResponse,
  selectFeature2LastCommandAction,
  selectFeature2ResumeChargingAt,
  selectFeature2ResumeChargingAtEnabled,
  selectFeature2StatusError,
  selectFeature2StatusLoading,
  selectFeature2VehicleStateError,
  selectFeature2VehicleStateLoading,
  selectFeature2VehicleStateResponse,
  selectFeature2ChargingSteeringVin,
} from '../../features/feature2ChargingSteering/selectors/feature2ChargingSteeringSelectors'
import {
  fetchTelemetryMessagesThunk,
  setTelemetryFrom,
  setVin as setTelemetryVin,
} from '../../features/chargingSteering/chargingSteeringSlice'
import {
  selectChargingSteeringTelemetryError,
  selectChargingSteeringTelemetryFrom,
  selectChargingSteeringTelemetryLoading,
  selectChargingSteeringTelemetryMessages,
  selectChargingSteeringVin as selectTelemetryVin,
} from '../../features/chargingSteering/selectors/chargingSteeringSelectors'
import { clampTelemetryFromValue } from '../../features/chargingSteering/utils/telemetryDate.utils'
import { selectCurrentVehicle } from '../../features/vehicle/selectors/vehicleSelectors'
import { useAppDispatch, useAppSelector } from '../../hooks'
import type { AppError } from '../../types/error.types'
import '../ChargingSteeringPage/styles.css'
import './styles.css'

type Feature2ChargingSteeringLocationState = {
  vin?: string
}

const Feature2ChargingSteeringPage: FC = () => {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()

  const vehicle = useAppSelector(selectCurrentVehicle)
  const vin = useAppSelector(selectFeature2ChargingSteeringVin)
  const commandLoading = useAppSelector(selectFeature2CommandLoading)
  const statusLoading = useAppSelector(selectFeature2StatusLoading)
  const vehicleStateLoading = useAppSelector(selectFeature2VehicleStateLoading)
  const resumeChargingAtEnabled = useAppSelector(selectFeature2ResumeChargingAtEnabled)
  const resumeChargingAt = useAppSelector(selectFeature2ResumeChargingAt)
  const commandIdInput = useAppSelector(selectFeature2CommandIdInput)
  const lastCommandAction = useAppSelector(selectFeature2LastCommandAction)
  const commandResponse = useAppSelector(selectFeature2CommandResponse)
  const commandStatusResponse = useAppSelector(selectFeature2CommandStatusResponse)
  const vehicleStateResponse = useAppSelector(selectFeature2VehicleStateResponse)
  const commandError = useAppSelector(selectFeature2CommandError)
  const statusError = useAppSelector(selectFeature2StatusError)
  const vehicleStateError = useAppSelector(selectFeature2VehicleStateError)

  const telemetryVin = useAppSelector(selectTelemetryVin)
  const telemetryFrom = useAppSelector(selectChargingSteeringTelemetryFrom)
  const telemetryMessages = useAppSelector(selectChargingSteeringTelemetryMessages)
  const telemetryLoading = useAppSelector(selectChargingSteeringTelemetryLoading)
  const telemetryError = useAppSelector(selectChargingSteeringTelemetryError)

  const targetVin = useMemo(() => {
    const stateVin = (location.state as Feature2ChargingSteeringLocationState | null)?.vin
    const queryVin = new URLSearchParams(location.search).get('vin')
    return (stateVin ?? queryVin ?? vehicle?.vin ?? '').trim().toUpperCase()
  }, [location.search, location.state, vehicle?.vin])

  const minimumResumeChargingAt = useMemo(() => getMinimumResumeChargingAt(), [])
  const selectedVin = vin.trim().toUpperCase()
  const selectedTelemetryVin = telemetryVin.trim().toUpperCase()
  const isVinReady = selectedVin.length > 0
  const commandBusy = commandLoading !== null

  useEffect(() => {
    if (targetVin && targetVin !== selectedVin) {
      dispatch(setFeature2Vin(targetVin))
    }
  }, [dispatch, selectedVin, targetVin])

  useEffect(() => {
    if (targetVin && targetVin !== selectedTelemetryVin) {
      dispatch(setTelemetryVin(targetVin))
    }
  }, [dispatch, selectedTelemetryVin, targetVin])

  useEffect(() => {
    if (!isVinReady || selectedTelemetryVin !== selectedVin) return
    if (targetVin && targetVin !== selectedVin) return

    void dispatch(fetchTelemetryMessagesThunk())
  }, [dispatch, isVinReady, selectedTelemetryVin, selectedVin, targetVin])

  const handleCommitTelemetryFrom = (value: string) => {
    if (!isVinReady) return

    const clampedFrom = clampTelemetryFromValue(value)
    if (clampedFrom !== telemetryFrom) {
      dispatch(setTelemetryFrom(clampedFrom))
    }

    void dispatch(fetchTelemetryMessagesThunk())
  }

  const handleSwitchToFeature3 = () => {
    navigate(`/charging-steering?vin=${encodeURIComponent(selectedVin)}`)
  }

  return (
    <div
      className="actions-page-wrapper feature2-steering-page"
      aria-label="Feature 2 charging steering page"
      role="region"
    >
      <div className="feature2-steering-vehicle-header">
        <VehicleHeader vehicle={vehicle} onChangeVehicle={() => navigate('/vehicles')} />
      </div>

      <SteeringModeBar mode="f2" onSwitch={handleSwitchToFeature3} />

      <section className="feature2-steering-section" aria-labelledby="feature2-command-title">
        <div className="feature2-steering-section-header">
          <h2 id="feature2-command-title">Charging Command</h2>
        </div>

        <ActionButtons
          disableActionButtons={!isVinReady || commandBusy}
          actionLoading={commandLoading}
          onStart={() => void dispatch(startFeature2ChargingThunk())}
          onStop={() => void dispatch(stopFeature2ChargingThunk())}
        />

        <div className="feature2-steering-resume-controls">
          <label className="feature2-steering-checkbox-label">
            <input
              type="checkbox"
              checked={resumeChargingAtEnabled}
              disabled={commandBusy}
              onChange={(event) =>
                dispatch(setFeature2ResumeChargingAtEnabled(event.target.checked))
              }
            />
            <span>Resume charging at</span>
          </label>

          <input
            type="datetime-local"
            className="feature2-steering-input feature2-steering-date-input"
            value={resumeChargingAt}
            min={minimumResumeChargingAt}
            disabled={!resumeChargingAtEnabled || commandBusy}
            aria-label="Resume charging at"
            onChange={(event) => dispatch(setFeature2ResumeChargingAt(event.target.value))}
          />
        </div>

        <InlineError title="Charging command failed" error={commandError} />
        <JsonResult
          title={`${lastCommandAction === 'stop' ? 'Stop' : 'Start'} command response`}
          value={commandResponse}
        />
      </section>

      <section className="feature2-steering-section" aria-labelledby="feature2-status-title">
        <div className="feature2-steering-section-header">
          <h2 id="feature2-status-title">Command Status</h2>
        </div>

        <div className="feature2-steering-field-action-row">
          <label className="feature2-steering-field">
            <span>Command ID</span>
            <input
              type="text"
              className="feature2-steering-input"
              value={commandIdInput}
              autoComplete="off"
              onChange={(event) => dispatch(setFeature2CommandIdInput(event.target.value))}
            />
          </label>

          <Button
            type="button"
            variant="secondary"
            disabled={!commandIdInput.trim() || statusLoading}
            onClick={() => void dispatch(fetchFeature2ChargingCommandStatusThunk())}
          >
            {statusLoading ? <LoadingIndicator style="INLINE" /> : 'Fetch Status'}
          </Button>
        </div>

        <InlineError title="Command status request failed" error={statusError} />
        <JsonResult title="Command status response" value={commandStatusResponse} />
      </section>

      <section className="feature2-steering-section" aria-labelledby="feature2-state-title">
        <div className="feature2-steering-section-header">
          <h2 id="feature2-state-title">Vehicle State</h2>
        </div>

        <Button
          type="button"
          variant="secondary"
          disabled={!isVinReady || vehicleStateLoading}
          onClick={() => void dispatch(fetchFeature2ChargingKeysThunk())}
        >
          {vehicleStateLoading ? <LoadingIndicator style="INLINE" /> : 'Fetch Vehicle State'}
        </Button>

        <InlineError title="Vehicle state request failed" error={vehicleStateError} />
        <JsonResult title="Vehicle state response" value={vehicleStateResponse} />
      </section>

      <TelemetryMessagesPanel
        from={telemetryFrom}
        messages={telemetryMessages}
        loading={telemetryLoading}
        error={telemetryError}
        isVinReady={isVinReady}
        onChangeFrom={(value) => dispatch(setTelemetryFrom(value))}
        onCommitFrom={handleCommitTelemetryFrom}
        onRefresh={() => handleCommitTelemetryFrom(telemetryFrom)}
      />
    </div>
  )
}

type JsonResultProps = {
  title: string
  value: unknown
}

const JsonResult: FC<JsonResultProps> = ({ title, value }) => {
  if (value === undefined) return null

  return (
    <div className="feature2-steering-result" aria-live="polite">
      <h3>{title}</h3>
      <pre>{formatJson(value)}</pre>
    </div>
  )
}

type InlineErrorProps = {
  title: string
  error?: AppError
}

const InlineError: FC<InlineErrorProps> = ({ title, error }) => {
  if (!error) return null

  return (
    <section className="actions-page-command-message" role="alert">
      <InfoBlock
        title={title}
        description={`${error.message}${error.requestId ? ` (Request ID: ${error.requestId})` : ''}`}
      />
    </section>
  )
}

const formatJson = (value: unknown): string => {
  try {
    return JSON.stringify(value, null, 2) ?? ''
  } catch {
    return 'Unable to display response.'
  }
}

const getMinimumResumeChargingAt = (): string => {
  const minimum = new Date(Date.now() + 11 * 60_000)
  minimum.setSeconds(0, 0)
  const timezoneOffsetMs = minimum.getTimezoneOffset() * 60_000
  return new Date(minimum.getTime() - timezoneOffsetMs).toISOString().slice(0, 16)
}

export default Feature2ChargingSteeringPage
